"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, ChartBar as BarChart3, Target, ShoppingBag, PhoneCall, Search, ArrowRight, ShieldCheck, Building2, Store, Briefcase } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface ProofMetric {
  id: number;
  stat: string;
  title: string;
  description: string;
  source: string;
  iconName: string;
  tag: string;
}
const proofMetrics: ProofMetric[] = [{
  id: 1,
  stat: "67%",
  title: "Median organic traffic increase",
  description: "Year-over-year organic traffic growth for UK SMEs investing in structured search campaigns.",
  source: "SEOCaddy Small Business SEO Impact Report 2025",
  iconName: "TrendingUp",
  tag: "Traffic growth"
}, {
  id: 2,
  stat: "312%",
  title: "Median return on investment",
  description: "Measured median ROI achieved across all small and medium business investment levels.",
  source: "SEOCaddy Small Business SEO Impact Report 2025",
  iconName: "BarChart3",
  tag: "Commercial ROI"
}, {
  id: 3,
  stat: "150%+",
  title: "Growth in conversion metrics",
  description: "Average lift in qualified enquiries, form submissions, and direct sales actions.",
  source: "SEOCaddy Small Business SEO Impact Report 2025",
  iconName: "Target",
  tag: "Conversions"
}, {
  id: 4,
  stat: "£43,700",
  title: "Single-month organic revenue",
  description: "Generated in a documented ecommerce project with a 10:1 return on SEO spend, scaling from 1,125 to 20,000 monthly UK visitors.",
  source: "Documented UK Retail Case Study",
  iconName: "ShoppingBag",
  tag: "Revenue impact"
}, {
  id: 5,
  stat: "40–80%",
  title: "Increase in local inbound enquiries",
  description: "Uplift in phone calls, quote requests, and physical footfall from targeted local search campaigns.",
  source: "SEOCaddy Local Market Analysis",
  iconName: "PhoneCall",
  tag: "Local search"
}, {
  id: 6,
  stat: "79%",
  title: "Clicks captured by page 1",
  description: "Share of all Google searches clicking top 10 positions, where top-ranking pages earn 3.8x more organic backlinks.",
  source: "Search Engine Authority & Backlink Benchmark",
  iconName: "Search",
  tag: "Market share"
}];
interface SegmentInsight {
  id: number;
  segment: string;
  heading: string;
  detail: string;
  iconName: string;
}
const segmentInsights: SegmentInsight[] = [{
  id: 1,
  segment: "Local Service Businesses",
  heading: "Inbound calls & Google Business visibility",
  detail: "Local SEO and Google Business Profile management consistently rank as top-ROI acquisition channels, delivering direct phone leads from your exact service area.",
  iconName: "Store"
}, {
  id: 2,
  segment: "Ecommerce Stores",
  heading: "Category architecture & product search",
  detail: "Category optimisation, product page technical fixes, and targeted link acquisition compound to drive recurring high-intent organic transactions.",
  iconName: "ShoppingBag"
}, {
  id: 3,
  segment: "B2B & Professional Services",
  heading: "High-value leads & long-tail search",
  detail: "Thought leadership content and precise long-tail commercial targeting generate qualified sales conversations at a fraction of long-term paid advertising costs.",
  iconName: "Briefcase"
}];
export default function Proof() {
  const prefersReducedMotion = useReducedMotion();
  const getMetricIcon = (name: string) => {
    switch (name) {
      case "TrendingUp":
        return <TrendingUp className="size-5 text-primary" />;
      case "BarChart3":
        return <BarChart3 className="size-5 text-primary" />;
      case "Target":
        return <Target className="size-5 text-primary" />;
      case "ShoppingBag":
        return <ShoppingBag className="size-5 text-primary" />;
      case "PhoneCall":
        return <PhoneCall className="size-5 text-primary" />;
      case "Search":
      default:
        return <Search className="size-5 text-primary" />;
    }
  };
  const getSegmentIcon = (name: string) => {
    switch (name) {
      case "Store":
        return <Store className="size-5 text-primary" />;
      case "ShoppingBag":
        return <ShoppingBag className="size-5 text-primary" />;
      case "Briefcase":
      default:
        return <Briefcase className="size-5 text-primary" />;
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        delay: prefersReducedMotion ? 0 : i * 0.08,
        ease: "easeOut"
      }
    })
  };
  return <section id="proofofresults" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1">
            Verified SEO Impact
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground tracking-tight mb-5">
            Proof my affordable SEO services deliver results
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Measurable commercial returns without agency overheads. Here is the concrete value and compounding growth cost-effective SEO delivers when every pound of your budget goes directly into expert strategy and execution.
          </p>
        </div>

        {/* Metric Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {proofMetrics.map((metric, i) => <motion.div key={metric.id} data-index={i} custom={i} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-40px"
        }} variants={cardVariants} className="h-full">
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        {getMetricIcon(metric.iconName)}
                      </div>
                      <Badge variant="outline" className="text-xs text-muted-foreground font-normal">
                        {metric.tag}
                      </Badge>
                    </div>
                    <div className="text-4xl sm:text-5xl font-serif font-bold text-primary tracking-tight mb-2">
                      {metric.stat}
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {metric.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 pb-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {metric.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-2 border-t border-border/60 mt-auto">
                  <p className="text-xs text-muted-foreground/80 flex items-center gap-1.5 font-mono">
                    <ShieldCheck className="size-3.5 text-primary shrink-0" />
                    <span>Source: {metric.source}</span>
                  </p>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Sector-Specific Performance Breakdown */}
        <div className="rounded-2xl bg-card border border-border p-8 sm:p-10 shadow-sm mb-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-6 border-b border-border">
            <div>
              <h3 className="text-xl sm:text-2xl font-serif font-semibold text-foreground">
                How results translate across your industry
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Strategic SEO creates distinct operational and financial advantages depending on your business model.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-full self-start md:self-auto shrink-0">
              <Building2 className="size-4" />
              <span>Real sector data</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {segmentInsights.map((insight, i) => <div key={insight.id} data-index={i} className="flex flex-col gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="size-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    {getSegmentIcon(insight.iconName)}
                  </div>
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                    {insight.segment}
                  </span>
                </div>
                <h4 className="text-base font-semibold text-card-foreground">
                  {insight.heading}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {insight.detail}
                </p>
              </div>)}
          </div>
        </div>

        {/* Verification Note & Action Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 sm:p-8 rounded-xl bg-secondary/50 border border-secondary text-secondary-foreground">
          <div className="flex items-start gap-3">
            <ShieldCheck className="size-6 text-primary shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-foreground">
                Verified data guarantee
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Every statistic and case study figure displayed above is drawn from documented UK SME performance studies and genuine client results. Never fabricated.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto shadow-sm">
              <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                <span>View real case studies</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}